/**
 * API Client Utility
 * Centralized API request handling with error management
 */

import { API_CONFIG, ERROR_MESSAGES } from '../config/constants'

/**
 * Creates a fetch request with timeout
 */
const fetchWithTimeout = (url, options, timeout = API_CONFIG.TIMEOUT) => {
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), timeout)
    ),
  ])
}

/**
 * Handles API response
 */
const handleResponse = async (response) => {
  const contentType = response.headers.get('content-type')
  
  if (!response.ok) {
    let errorMessage = ERROR_MESSAGES.GENERIC_ERROR
    
    if (contentType && contentType.includes('application/json')) {
      const errorData = await response.json()
      errorMessage = errorData.error || errorData.message || errorMessage
    } else {
      errorMessage = await response.text() || errorMessage
    }
    
    const error = new Error(errorMessage)
    error.status = response.status
    error.statusText = response.statusText
    throw error
  }
  
  if (contentType && contentType.includes('application/json')) {
    return await response.json()
  }
  
  return await response.text()
}

/**
 * Main API client function
 */
export const apiClient = async (endpoint, options = {}) => {
  const url = `${API_CONFIG.BASE_URL}${endpoint}`
  
  // Get admin token if available
  const adminToken = localStorage.getItem('adminToken')
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(adminToken && { Authorization: `Bearer ${adminToken}` }),
    },
  }
  
  const config = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  }
  
  try {
    const response = await fetchWithTimeout(url, config)
    return await handleResponse(response)
  } catch (error) {
    // Handle network errors
    if (error.message === 'Request timeout') {
      throw new Error('Request timed out. Please try again.')
    }
    
    if (error.name === 'TypeError' || error.message.includes('fetch')) {
      throw new Error(ERROR_MESSAGES.NETWORK_ERROR)
    }
    
    throw error
  }
}

/**
 * Convenience methods for common HTTP methods
 */
export const api = {
  get: (endpoint, options = {}) =>
    apiClient(endpoint, { ...options, method: 'GET' }),
  
  post: (endpoint, data, options = {}) =>
    apiClient(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  put: (endpoint, data, options = {}) =>
    apiClient(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (endpoint, options = {}) =>
    apiClient(endpoint, { ...options, method: 'DELETE' }),
  
  patch: (endpoint, data, options = {}) =>
    apiClient(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
}

export default apiClient
